import React, { useState } from 'react';
import { addDoc, collection, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../config/firebase.js';
import logger from '../../utils/logger';
import { SUBJECTS } from '../../utils/catalogue';
import { moderatorName } from '../../utils/useModerators';

const MAX_PHOTO_SIZE = 2 * 1024 * 1024;
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export const TYPE_LABELS = { founder: 'Founder', team: 'Team member', moderator: 'Moderator' };

const inputClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none';

/**
 * REQ-03 — create or edit a biography card.
 * Administrators edit every field. A moderator editing their own card can
 * change its content (name, title, subject areas, biography, photograph)
 * but not its type, link, order or visibility — the Firestore rules enforce
 * the same split.
 */
const ProfileEditor = ({ profile, defaultType = 'moderator', nextOrder = 0, isAdmin, moderators = [], actor, onClose, onSaved }) => {
  const isNew = !profile?.id;
  const [form, setForm] = useState({
    type: profile?.type || defaultType,
    name: profile?.name || '',
    title: profile?.title || '',
    subjects: profile?.subjects || [],
    bio: profile?.bio || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    userId: profile?.userId || '',
    visible: profile?.visible ?? true,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(profile?.photoUrl || '');
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleSubject = (label) =>
    set('subjects', form.subjects.includes(label) ? form.subjects.filter((s) => s !== label) : [...form.subjects, label]);

  const choosePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      setError('Photographs must be JPEG, PNG or WebP images.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      setError('Photographs must be 2 MB or smaller.');
      return;
    }
    setError('');
    setPhotoFile(file);
    setRemovePhoto(false);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('A name is required.');
      return;
    }
    setSaving(true);
    setError('');

    const content = {
      name: form.name.trim(),
      title: form.title.trim(),
      subjects: form.subjects,
      bio: form.bio.trim(),
      updatedAt: Timestamp.now(),
      updatedBy: actor,
    };
    const adminFields = isAdmin
      ? {
          type: form.type,
          email: form.email.trim(),
          phone: form.phone.trim(),
          userId: form.type === 'moderator' ? form.userId || null : null,
          visible: form.visible,
        }
      : {};

    try {
      let id = profile?.id;
      if (isNew) {
        const created = await addDoc(collection(db, 'profiles'), {
          ...content,
          ...adminFields,
          order: nextOrder,
          photoUrl: '',
          photoPath: '',
          createdAt: Timestamp.now(),
        });
        id = created.id;
      } else {
        await updateDoc(doc(db, 'profiles', id), { ...content, ...adminFields });
      }

      const oldPath = profile?.photoPath;
      if (photoFile) {
        const safeName = photoFile.name.replace(/[^\w.-]+/g, '_');
        const path = `site/profile-photos/${id}/${Date.now()}-${safeName}`;
        const photoRef = ref(storage, path);
        await uploadBytes(photoRef, photoFile, { contentType: photoFile.type });
        const url = await getDownloadURL(photoRef);
        await updateDoc(doc(db, 'profiles', id), { photoUrl: url, photoPath: path });
        if (oldPath) await deleteObject(ref(storage, oldPath)).catch(() => {});
      } else if (removePhoto) {
        await updateDoc(doc(db, 'profiles', id), { photoUrl: '', photoPath: '' });
        if (oldPath) await deleteObject(ref(storage, oldPath)).catch(() => {});
      }

      onSaved?.(isNew ? 'Card created.' : 'Card saved.');
      onClose();
    } catch (err) {
      logger.error('Error saving profile:', err);
      setError(err?.code === 'permission-denied' ? 'You do not have permission to change this card.' : 'Could not save. Please try again.');
      setSaving(false);
    }
  };

  const initials = (form.name || '?').split(' ').map((n) => n.charAt(0)).slice(0, 2).join('').toUpperCase();

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={() => !saving && onClose()} />
      <form
        onSubmit={save}
        role="dialog"
        aria-modal="true"
        className="relative z-[1001] w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-xl"
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">
            {isNew ? `Add ${TYPE_LABELS[form.type].toLowerCase()} card` : isAdmin ? 'Edit card' : 'Edit my biography'}
          </h3>
          <button type="button" onClick={onClose} disabled={saving} className="h-9 w-9 rounded-full text-gray-500 hover:bg-gray-100" aria-label="Close">✕</button>
        </div>

        <div className="p-6 space-y-5">
          {/* Photograph */}
          <div className="flex items-center gap-4">
            {photoPreview && !removePhoto ? (
              <img src={photoPreview} alt="" className="w-20 h-20 rounded-full object-cover ring-2 ring-blue-100" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-bold ring-2 ring-blue-100">{initials}</div>
            )}
            <div className="space-y-1">
              <label className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 cursor-pointer">
                Upload photograph
                <input type="file" accept={PHOTO_TYPES.join(',')} onChange={choosePhoto} className="hidden" />
              </label>
              {(photoPreview && !removePhoto) && (
                <button type="button" onClick={() => { setRemovePhoto(true); setPhotoFile(null); }} className="block text-xs text-gray-500 hover:text-red-600">
                  Remove photograph
                </button>
              )}
              <p className="text-xs text-gray-400">JPEG, PNG or WebP, up to 2 MB.</p>
            </div>
          </div>

          {isAdmin && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Card type</label>
                <select value={form.type} onChange={(e) => set('type', e.target.value)} className={`${inputClass} bg-white`}>
                  {Object.entries(TYPE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-700 sm:mt-7">
                <input type="checkbox" checked={form.visible} onChange={(e) => set('visible', e.target.checked)} />
                Show on the About Us page
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Name</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputClass} placeholder="e.g. Dr Jane Doe" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Role or title</label>
              <input value={form.title} onChange={(e) => set('title', e.target.value)} className={inputClass} placeholder="e.g. Senior Lecturer, Mathematics Education" />
            </div>
          </div>

          {isAdmin && form.type !== 'moderator' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone</label>
                <input value={form.phone} onChange={(e) => set('phone', e.target.value)} className={inputClass} />
              </div>
            </div>
          )}

          {isAdmin && form.type === 'moderator' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Linked moderator account</label>
              <select value={form.userId} onChange={(e) => set('userId', e.target.value)} className={`${inputClass} bg-white`}>
                <option value="">Not linked</option>
                {moderators.map((m) => <option key={m.id} value={m.id}>{moderatorName(m)} ({m.email})</option>)}
                {form.userId && !moderators.some((m) => m.id === form.userId) && (
                  <option value={form.userId}>Former moderator account</option>
                )}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                A linked moderator can edit this card. It is hidden automatically if they stop being a moderator.
              </p>
            </div>
          )}

          {form.type === 'moderator' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Subject areas</label>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                {SUBJECTS.map((s) => (
                  <button
                    type="button"
                    key={s.subject}
                    onClick={() => toggleSubject(s.label)}
                    className={`rounded-full border px-2 py-0.5 text-xs ${
                      form.subjects.includes(s.label) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Biography</label>
            <textarea value={form.bio} onChange={(e) => set('bio', e.target.value)} rows={7} className={inputClass} />
          </div>

          {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        </div>

        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditor;
